import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { parseSimpleText } from '@hcd/domain'
import {
  Ban,
  CalendarCheck,
  CalendarClock,
  ChevronLeft,
  CircleCheck,
  CircleDashed,
  CircleX,
  FileSignature,
  History,
  ListChecks,
  Loader2,
  LogOut,
  Printer,
  Receipt,
  Save,
  TriangleAlert,
} from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { annualLine, contractMoney, ContractStatusBadge } from '@/components/contracts/views'
import { Panel } from '@/components/quotes/quote-panels'
import { FormField, NativeSelect, TextInput } from '@/components/settings/form-kit'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  CONTRACT_WRITERS,
  contractQuery,
  daysUntil,
  useContractMutation,
  type ContractDetail,
} from '@/lib/contracts'
import { ukDate } from '@/lib/quotes'
import { resourceQuery, type ContractType } from '@/lib/settings'
import { useTitle } from '@/lib/use-title'

export const Route = createFileRoute('/_app/contracts/$id')({ component: ContractPage })

type DialogKind = null | 'notice' | 'end' | 'cancel' | 'review'

const today = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date())

function ContractPage() {
  const { id } = Route.useParams()
  const { me } = Route.useRouteContext()
  const { data: c, isPending, isError, error } = useQuery(contractQuery(id))
  const [dialog, setDialog] = useState<DialogKind>(null)
  useTitle(c ? `${c.reference} ${c.customerName}` : 'Contract')

  if (isError)
    return (
      <p role="alert" className="text-destructive">
        {error.message}
      </p>
    )
  if (isPending) return <Skeleton className="h-[70dvh] rounded-xl" />

  const canEdit = me.roles.some((r) => CONTRACT_WRITERS.includes(r))
  const live = c.status === 'active' || c.status === 'notice_given'

  return (
    <>
      <Link
        to="/contracts"
        className="-ml-2 mb-2 inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Contracts
      </Link>
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="tabular text-sm font-semibold text-muted-foreground">
            {c.reference}
            {c.contractTypeName && ` · ${c.contractTypeName}`}
          </p>
          <h1 className="mt-0.5 flex flex-wrap items-center gap-3 text-2xl font-semibold tracking-tight">
            <span className="min-w-0 break-words">{c.customerName}</span>
            <ContractStatusBadge status={c.status} label={c.statusLabel} />
          </h1>
          <p className="mt-1 text-[15px] text-muted-foreground">
            {[c.siteName, c.title].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <Button
            variant="outline"
            className="h-10 flex-1 sm:flex-none"
            render={<a href={`/contracts/${c.id}/print`} target="_blank" rel="noopener" />}
          >
            <Printer />
            Signed copy
          </Button>
          {canEdit && c.status === 'active' && (
            <Button
              variant="outline"
              className="h-10 flex-1 sm:flex-none"
              onClick={() => setDialog('review')}
            >
              <CalendarCheck />
              Review done
            </Button>
          )}
          {canEdit && c.status === 'active' && (
            <Button
              variant="outline"
              className="h-10 flex-1 sm:flex-none"
              onClick={() => setDialog('notice')}
            >
              <LogOut />
              Give notice
            </Button>
          )}
          {canEdit && live && (
            <Button
              variant="outline"
              className="h-10 flex-1 sm:flex-none"
              onClick={() => setDialog('end')}
            >
              <CircleX />
              End
            </Button>
          )}
          {canEdit && live && (
            <Button
              variant="ghost"
              className="h-10 flex-1 text-destructive hover:text-destructive sm:flex-none"
              onClick={() => setDialog('cancel')}
            >
              <Ban />
              Cancel contract
            </Button>
          )}
        </div>
      </header>

      <StatusBanner c={c} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <div className="grid min-w-0 content-start gap-4">
          <AgreementPanel c={c} />
          <DatesPanel c={c} canEdit={canEdit} />
          <HandoffSummary c={c} />
        </div>
        <div className="grid min-w-0 content-start gap-4">
          <BillingPanel c={c} />
          <Timeline c={c} />
        </div>
      </div>

      <ActionDialogs c={c} dialog={dialog} setDialog={setDialog} />
    </>
  )
}

function StatusBanner({ c }: { c: ContractDetail }) {
  let tone = ''
  let text: ReactNode = null
  if (c.status === 'notice_given' && c.endDate) {
    const d = daysUntil(c.endDate)!
    tone = 'border-warning/40 bg-warning/10'
    text = (
      <>
        Notice given on {ukDate(c.noticeGivenOn)}. The contract {d < 0 ? 'ended' : 'ends'} on{' '}
        <strong>{ukDate(c.endDate)}</strong>
        {d >= 0 && ` (${d === 0 ? 'today' : d === 1 ? 'tomorrow' : `in ${d} days`})`}.
        {c.endReason && <span className="block text-muted-foreground">Reason: {c.endReason}</span>}
      </>
    )
  } else if (c.status === 'ended' || c.status === 'cancelled') {
    tone = 'border-border bg-muted'
    text = (
      <>
        {c.status === 'ended' ? 'Ended' : 'Cancelled'} on {ukDate(c.endDate)}.
        {c.endReason && <span className="block text-muted-foreground">Reason: {c.endReason}</span>}
      </>
    )
  } else if (c.reviewDue && c.reviewDate) {
    const d = daysUntil(c.reviewDate)!
    tone = 'border-warning/40 bg-warning/10'
    text =
      d < 0 ? (
        <>Review overdue since {ukDate(c.reviewDate)}. Check pricing and scope with the client.</>
      ) : (
        <>Review due {ukDate(c.reviewDate)}. Check pricing and scope with the client.</>
      )
  } else if (!c.startDate) {
    tone = 'border-primary/25 bg-accent/40'
    text = (
      <>
        No start date yet. It is set when the client finishes onboarding, or you can set it below.
      </>
    )
  }
  if (!text) return null
  return (
    <p className={`mb-4 flex gap-2 rounded-xl border p-4 text-[15px] ${tone}`} role="status">
      <CalendarClock className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
      <span>{text}</span>
    </p>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="text-[15px] break-words">{children}</dd>
    </div>
  )
}

function AgreementPanel({ c }: { c: ContractDetail }) {
  const blocks = parseSimpleText(c.agreement.text)
  return (
    <Panel title="Agreement" icon={<FileSignature />}>
      <dl className="grid gap-3 sm:grid-cols-2">
        <Row label="Value">
          <span className="tabular font-medium">{contractMoney(c)}</span>
          <span className="tabular block text-sm text-muted-foreground">{annualLine(c)}</span>
        </Row>
        <Row label="From quote">
          <Link
            to="/quotes/$id"
            params={{ id: c.quoteId }}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {c.quoteReference}
            {c.revision && c.revision > 1 ? ` (revision ${c.revision})` : ''}
          </Link>
        </Row>
        <Row label="Signed by the client">
          {c.signatures.client
            ? `${c.signatures.client.name}, ${c.signatures.client.position}`
            : c.clientSignerName || 'Not recorded'}
          <span className="block text-sm text-muted-foreground">{ukDate(c.signedAt, true)}</span>
        </Row>
        <Row label="Signed for HCD">
          {c.signatures.hcd ? `${c.signatures.hcd.name}, ${c.signatures.hcd.role}` : 'Not recorded'}
          {c.signatures.hcd && (
            <span className="block text-sm text-muted-foreground">
              {ukDate(c.signatures.hcd.signedAt, true)}
            </span>
          )}
        </Row>
      </dl>
      {blocks.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium text-primary">
            Agreement as signed
          </summary>
          <div className="mt-3 grid gap-3 rounded-lg bg-muted p-4 text-sm">
            {blocks.map((b, i) =>
              b.type === 'heading' ? (
                <h3 key={i} className="font-semibold">
                  {b.text}
                </h3>
              ) : (
                <p key={i}>{b.text}</p>
              ),
            )}
          </div>
        </details>
      )}
      {c.evidence && (
        <p className="mt-4 text-[13px] break-all text-muted-foreground">
          Fingerprint (SHA-256) {c.evidence.contentHash}
        </p>
      )}
    </Panel>
  )
}

function DatesPanel({ c, canEdit }: { c: ContractDetail; canEdit: boolean }) {
  const save = useContractMutation(c.id)
  const types = useQuery({ ...resourceQuery<ContractType>('contract-types'), enabled: canEdit })
  const initial = () => ({
    startDate: c.startDate ?? '',
    reviewDate: c.reviewDate ?? '',
    noticePeriodDays: String(c.noticePeriodDays),
    contractTypeId: c.contractTypeId ?? '',
    notes: c.notes,
  })
  const [f, setF] = useState(initial)
  useEffect(() => setF(initial()), [c.version]) // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }))
  const days = Number(f.noticePeriodDays)
  const noticeError =
    f.noticePeriodDays.trim() === '' || !Number.isInteger(days) || days < 0 || days > 365
      ? 'Enter a whole number of days from 0 to 365.'
      : null
  const dirty = JSON.stringify(f) !== JSON.stringify(initial())
  const editable = canEdit && (c.status === 'active' || c.status === 'notice_given')

  if (!editable)
    return (
      <Panel title="Dates and review" icon={<CalendarClock />}>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Row label="Start date">{ukDate(c.startDate)}</Row>
          <Row label="Next review">{ukDate(c.reviewDate)}</Row>
          <Row label="Notice period">{c.noticePeriodDays} days</Row>
          <Row label="End date">{ukDate(c.endDate)}</Row>
          {c.notes && (
            <div className="sm:col-span-2">
              <Row label="Notes">
                <span className="whitespace-pre-line">{c.notes}</span>
              </Row>
            </div>
          )}
        </dl>
      </Panel>
    )

  return (
    <Panel title="Dates and review" icon={<CalendarClock />}>
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (noticeError) return
          save.mutate({
            path: '',
            success: 'Contract saved',
            body: {
              ...(f.startDate !== (c.startDate ?? '') ? { startDate: f.startDate } : {}),
              ...(f.reviewDate !== (c.reviewDate ?? '') ? { reviewDate: f.reviewDate } : {}),
              ...(days !== c.noticePeriodDays ? { noticePeriodDays: days } : {}),
              ...(f.contractTypeId !== (c.contractTypeId ?? '')
                ? { contractTypeId: f.contractTypeId || null }
                : {}),
              ...(f.notes !== c.notes ? { notes: f.notes } : {}),
              expectedVersion: c.version,
            },
          })
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Start date" hint="Changing it moves the first review to 12 months on.">
            {(p) => (
              <TextInput
                {...p}
                type="date"
                value={f.startDate}
                onChange={(e) => set({ startDate: e.target.value })}
              />
            )}
          </FormField>
          <FormField label="Next review">
            {(p) => (
              <TextInput
                {...p}
                type="date"
                value={f.reviewDate}
                onChange={(e) => set({ reviewDate: e.target.value })}
              />
            )}
          </FormField>
          <FormField label="Notice period (days)" error={noticeError}>
            {(p) => (
              <TextInput
                {...p}
                inputMode="numeric"
                value={f.noticePeriodDays}
                onChange={(e) => set({ noticePeriodDays: e.target.value.replace(/[^\d]/g, '') })}
              />
            )}
          </FormField>
          <FormField label="Contract type">
            {(p) => (
              <NativeSelect
                {...p}
                value={f.contractTypeId}
                onChange={(e) => set({ contractTypeId: e.target.value })}
              >
                <option value="">Not set</option>
                {(types.data ?? [])
                  .filter((t) => t.active || t.id === c.contractTypeId)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
              </NativeSelect>
            )}
          </FormField>
        </div>
        <FormField label="Notes" hint="For HCD only. Clients never see these.">
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              className="text-base md:text-[15px]"
              value={f.notes}
              onChange={(e) => set({ notes: e.target.value })}
            />
          )}
        </FormField>
        {c.status === 'notice_given' && (
          <p className="text-[13px] text-muted-foreground">
            Notice has been given, so changing the notice period moves the end date.
          </p>
        )}
        <div>
          <Button
            type="submit"
            className="h-10"
            disabled={!dirty || !!noticeError || save.isPending}
          >
            {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
            Save changes
          </Button>
        </div>
      </form>
    </Panel>
  )
}

function BillingPanel({ c }: { c: ContractDetail }) {
  const b = c.billing
  return (
    <Panel title="Who to invoice" icon={<Receipt />}>
      {b ? (
        <dl className="grid gap-3">
          <Row label="Accounts contact">{b.contactName || 'Not given'}</Row>
          <Row label="Email">
            {b.email ? (
              <a
                href={`mailto:${b.email}`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {b.email}
              </a>
            ) : (
              'Not given'
            )}
          </Row>
          {b.phone && <Row label="Phone">{b.phone}</Row>}
          <Row label="Invoice address">
            <span className="whitespace-pre-line">{b.address || 'Not given'}</span>
          </Row>
          {b.poReference && <Row label="PO reference">{b.poReference}</Row>}
          {b.notes && (
            <Row label="Notes for invoicing">
              <span className="whitespace-pre-line">{b.notes}</span>
            </Row>
          )}
          {c.customer?.vatNumber && <Row label="Client VAT number">{c.customer.vatNumber}</Row>}
        </dl>
      ) : (
        <p className="text-[15px] text-muted-foreground">
          Waiting for the client to complete onboarding with their accounts details.
        </p>
      )}
      {c.site && (
        <dl className="mt-4 grid gap-3 border-t pt-4">
          <Row label="Site">
            {c.site.name}
            <span className="block text-sm text-muted-foreground">{c.site.address}</span>
          </Row>
          {(c.site.contactName || c.site.contactPhone) && (
            <Row label="Site contact">
              {[c.site.contactName, c.site.contactPhone].filter(Boolean).join(', ')}
            </Row>
          )}
        </dl>
      )}
    </Panel>
  )
}

const TASK_ICON: Record<string, ReactNode> = {
  done: <CircleCheck className="size-4 text-success" aria-hidden />,
  skipped: <CircleCheck className="size-4 text-muted-foreground" aria-hidden />,
  failed: <TriangleAlert className="size-4 text-destructive" aria-hidden />,
}
const TASK_LABEL: Record<string, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
  failed: 'Problem',
  skipped: 'Not needed',
}

function HandoffSummary({ c }: { c: ContractDetail }) {
  if (!c.handoff.total) return null
  return (
    <Panel title={`Admin set-up (${c.handoff.done} of ${c.handoff.total})`} icon={<ListChecks />}>
      <ul className="grid gap-2.5">
        {c.handoff.tasks.map((t) => (
          <li key={t.id} className="flex items-start gap-2.5 text-[15px]">
            <span className="mt-0.5">
              {TASK_ICON[t.status] ?? (
                <CircleDashed className="size-4 text-muted-foreground" aria-hidden />
              )}
            </span>
            <span className="min-w-0 flex-1">
              {t.title}
              <span className="block text-sm text-muted-foreground">
                {TASK_LABEL[t.status] ?? t.status}
                {t.externalRef && ` · ${t.externalRef}`}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <Link
        to="/quotes/$id"
        params={{ id: c.quoteId }}
        className="mt-4 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
      >
        Update tasks on the quote
      </Link>
    </Panel>
  )
}

const EVENT_LABEL: Record<string, string> = {
  created: 'Contract filed after the client signed',
  start_date_set: 'Start date set from client onboarding',
  updated: 'Details changed',
  reviewed: 'Review done',
  notice_given: 'Notice given',
  ended: 'Ended',
  cancelled: 'Cancelled',
}

function Timeline({ c }: { c: ContractDetail }) {
  return (
    <Panel title="Timeline" icon={<History />}>
      <ol className="grid gap-4">
        {c.events.map((e) => {
          const detail = [e.detail.title, e.detail.reason].filter(Boolean).join('. ')
          return (
            <li key={e.id} className="text-sm">
              <span className="block font-medium">{EVENT_LABEL[e.type] ?? e.type}</span>
              <span className="block text-muted-foreground">
                {e.actorLabel ? `${e.actorLabel} · ` : ''}
                {ukDate(e.at, true)}
              </span>
              {detail && e.type !== 'created' && (
                <span className="mt-1 block rounded-md bg-muted px-2 py-1">{String(detail)}</span>
              )}
            </li>
          )
        })}
      </ol>
    </Panel>
  )
}

function ActionDialogs({
  c,
  dialog,
  setDialog,
}: {
  c: ContractDetail
  dialog: DialogKind
  setDialog: (d: DialogKind) => void
}) {
  const act = useContractMutation(c.id)
  const [date, setDate] = useState(today())
  const [reason, setReason] = useState('')
  useEffect(() => {
    if (dialog) {
      setDate(dialog === 'end' && c.status === 'notice_given' && c.endDate ? c.endDate : today())
      setReason('')
    }
  }, [dialog, c.status, c.endDate])

  const addDays = (d: string, n: number) => {
    const x = new Date(`${d}T12:00:00Z`)
    x.setUTCDate(x.getUTCDate() + n)
    return x.toISOString().slice(0, 10)
  }
  const cfg = {
    notice: {
      title: 'Record notice',
      description: `The contract ends ${c.noticePeriodDays} days after notice is given.`,
      action: 'Record notice',
      path: 'notice',
      success: 'Notice recorded',
      dateLabel: 'Notice given on',
      reasonLabel: 'Reason',
      needsReason: true,
      body: () => ({ givenOn: date, reason }),
    },
    end: {
      title: 'End this contract?',
      description: 'Use this when the service has stopped. The contract moves to Ended.',
      action: 'End contract',
      path: 'end',
      success: 'Contract ended',
      dateLabel: 'Last day of service',
      reasonLabel: 'Reason',
      needsReason: true,
      body: () => ({ endDate: date, reason }),
    },
    cancel: {
      title: 'Cancel this contract?',
      description: 'For example if the client pulls out before the service starts.',
      action: 'Cancel contract',
      path: 'cancel',
      success: 'Contract cancelled',
      dateLabel: null,
      reasonLabel: 'Reason',
      needsReason: true,
      body: () => ({ reason }),
    },
    review: {
      title: 'Record review',
      description: `The next review will be 12 months after ${ukDate(c.reviewDate ?? today())}.`,
      action: 'Record review',
      path: 'review',
      success: 'Review recorded',
      dateLabel: null,
      reasonLabel: 'What was agreed (optional)',
      needsReason: false,
      body: () => ({ note: reason }),
    },
  } as const
  const d = dialog ? cfg[dialog] : null
  const reasonOk = !d?.needsReason || reason.trim().length >= 3
  const dateOk = !d?.dateLabel || /^\d{4}-\d{2}-\d{2}$/.test(date)

  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
      <DialogContent className="sm:max-w-md">
        {d && (
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              if (!reasonOk || !dateOk) return
              act.mutate(
                {
                  path: d.path,
                  body: { ...d.body(), expectedVersion: c.version },
                  success: d.success,
                },
                { onSuccess: () => setDialog(null) },
              )
            }}
          >
            <DialogHeader>
              <DialogTitle>{d.title}</DialogTitle>
              <DialogDescription>{d.description}</DialogDescription>
            </DialogHeader>
            {d.dateLabel && (
              <FormField
                label={d.dateLabel}
                hint={
                  dialog === 'notice' && dateOk
                    ? `The contract will end on ${ukDate(addDays(date, c.noticePeriodDays))}.`
                    : undefined
                }
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="date"
                    required
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                )}
              </FormField>
            )}
            <FormField label={d.reasonLabel}>
              {(p) => (
                <Textarea
                  {...p}
                  rows={3}
                  required={d.needsReason}
                  className="text-base md:text-[15px]"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
            </FormField>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-10"
                onClick={() => setDialog(null)}
              >
                Go back
              </Button>
              <Button
                type="submit"
                className="h-10"
                variant={dialog === 'cancel' || dialog === 'end' ? 'destructive' : 'default'}
                disabled={!reasonOk || !dateOk || act.isPending}
              >
                {act.isPending && <Loader2 className="animate-spin" />}
                {d.action}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
